import React, { useState, useEffect } from 'react';
import { MdOutlineRefresh } from 'react-icons/md';
import { FiTruck } from 'react-icons/fi';
import DatLogo from '../../assets/dat-tm-logo.svg';

import Header from '../../layouts/Header';
import Filter from '../../helpers/filter/Filter';
import DATPost from '../../features/creates/datPost/DAT';
import Create from '../../helpers/panel/Create';
import FilterData from '../../helpers/filter/FilterData';
import BBITable from '../../helpers/bBITable/BBITable';
import classes from './DatTruckBoard.module.css';
import Get from '../../api/internal/Get';
import Delete from '../../api/internal/Delete';
import TransportationCaller from '../../api/internal/TransportationCaller';
import ViewDATPost from '../../features/views/assets/dat/DATPost';
import View from '../../helpers/slab/View';
import { PageLoadingSpinner } from '../../helpers/loadingSpinner/LoadingSpinner';

const DAT_BOARD_URL = process.env.REACT_APP_DAT_BOARD_URL;

//dat posts can only be refreshed once every 15 minutes
const REFRESH_COOLDOWN_MS = 15 * 60 * 1000;

type PostLoadingState = {
  refresh: Record<string, boolean>; // false = loading, undefined = idle
  delete: Record<string, boolean>; // false = loading, undefined = idle
};

function DatTruckBoard({ toggleBoardRefresh }) {
  const [filterValue, setFilterValue] = useState('');
  const [datPostBoard, setDATTruckPostBoard] = useState<
    Array<DATTruckPostBoardItem>
  >([]);
  const [loadingState, setLoadingState] = useState<PostLoadingState>({
    refresh: {},
    delete: {},
  });
  const [isBoardLoading, setIsBoardLoading] = useState<boolean>(false);

  useEffect(() => {
    GetDATTruckPostBoard();
  }, [toggleBoardRefresh]);

  function GetDATTruckPostBoard() {
    setIsBoardLoading(true);

    Get(`/DatService/GetDatTruckPostBoard`)
      .then(response => {
        if (response) {
          setDATTruckPostBoard(response.data);
        }
      })
      .finally(() => {
        setIsBoardLoading(false);
      });
  }

  function RefreshDATTruckPost(datId: string) {
    setLoadingState(prev => ({
      ...prev,
      refresh: { ...prev.refresh, [datId]: false },
    }));
    TransportationCaller.post(`/DatService/RefreshDatTruckPost/${datId}`).then(
      response => {
        if (response) {
          GetDATTruckPostBoard();
          setLoadingState(prev => ({
            ...prev,
            refresh: { ...prev.refresh, [datId]: true },
          }));
        }
      },
    );
  }

  function DeleteDATTruckPost(datId: string) {
    setLoadingState(prev => ({
      ...prev,
      delete: { ...prev.delete, [datId]: false },
    }));

    Delete(`/DatService/DeleteDatTruckPost/${datId}`).then(
      () => {
        GetDATTruckPostBoard();
      },
      () => {
        setLoadingState(prev => ({
          ...prev,
          delete: { ...prev.delete, [datId]: true },
        }));
      },
    );
  }

  const tableData = FilterData(datPostBoard, filterValue);

  const renderRefreshControl = (post: DATTruckPostBoardItem) => {
    const lastUpdated = new Date(post.lastUpdated).getTime();
    const timeLeft = Math.max(
      0,
      REFRESH_COOLDOWN_MS - (Date.now() - lastUpdated),
    );
    const minutesRemaining = Math.ceil(timeLeft / 60000);
    const countdownDisplay =
      minutesRemaining > 0 ? `${minutesRemaining} min` : null;

    if (loadingState.refresh[post.datId] === false) {
      return (
        <span
          className={classes.refreshingText}
          title="Post is being refreshed"
        >
          Refreshing...
        </span>
      );
    }

    if (timeLeft === 0) {
      return (
        <span
          onClick={() => RefreshDATTruckPost(post.datId)}
          className={`${classes.refreshIconWrapper}`}
          title="Refresh Post"
        >
          <MdOutlineRefresh />
        </span>
      );
    }

    return (
      <span className={classes.countdownText} title="Refresh available in">
        {countdownDisplay}
      </span>
    );
  };

  const renderDeleteControl = (post: DATTruckPostBoardItem) => {
    const isDeleting = loadingState.delete[post.datId] === false;

    return !isDeleting ? (
      <span
        title={'Delete Post'}
        className={classes.deleteIcon}
        onClick={!isDeleting ? () => DeleteDATTruckPost(post.datId) : null}
      >
        ❌
      </span>
    ) : (
      <span className={classes.deleteIconDisabled} title="Deleting Post">
        Deleting...
      </span>
    );
  };

  const newData = tableData?.map((post: DATTruckPostBoardItem) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
    };
    const posted = new Date(post.posted);
    const formattedDate = posted
      .toLocaleString(undefined, options)
      .replace(',', ' @');

    return {
      ...post,
      equipment: `${post.datEquipmentName} ${post.length}ft`,
      posted: `${formattedDate}`,
      origin: `${post.originCity}${
        post?.originState ? `, ${post.originState}` : ''
      }`,
      destination: `${post?.destinationCity}${
        post?.destinationState ? `, ${post.destinationState}` : ''
      }`,
      refresh: renderRefreshControl(post),
      delete: renderDeleteControl(post),
    };
  });

  const columnConfig = [
    {
      key: '1',
      attribute: 'posted',
      attributeprimaryhidden: 'datId',
      header: 'Posted',
      onClick: e =>
        View(<ViewDATPost datId={e.target.dataset.attributeprimaryhidden} />),
    },
    {
      key: '2',
      attribute: 'equipment',
      attributeprimaryhidden: 'datId',
      header: 'Equipment Type',
      onClick: e =>
        View(<ViewDATPost datId={e.target.dataset.attributeprimaryhidden} />),
    },
    {
      key: '3',
      attribute: 'weight',
      header: 'Weight (lbs)',
    },
    {
      key: '4',
      attribute: 'origin',
      header: 'Origin',
    },
    {
      key: '5',
      attribute: 'destination',
      header: 'Destination',
    },
    {
      key: '6',
      attribute: 'earliestAvailableDate',
      header: 'Earliest Availability',
    },
    {
      key: '7',
      attribute: 'latestAvailableDate',
      header: 'Latest Availability',
    },
    {
      key: '8',
      attribute: 'comment',
      header: 'Comment',
    },
    {
      key: '9',
      attribute: 'refresh',
      header: 'Refresh Post',
    },
    {
      key: '10',
      attribute: 'delete',
      header: 'Delete Post',
    },
  ];

  return (
    <>
      <a
        href={DAT_BOARD_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.datLogo}
      >
        <img src={DatLogo} alt="DAT Logo" />
      </a>
      <Header>
        <div>
          <button
            onClick={() =>
              Create(<DATPost GetTruckBoard={GetDATTruckPostBoard} />)
            }
          >
            <FiTruck />
            <span>Post a Truck</span>
          </button>
          <button onClick={GetDATTruckPostBoard}>
            <MdOutlineRefresh />
            <span>Refresh Board</span>
          </button>
        </div>
        <div>
          <Filter
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            placeholder="Filter posts"
          />
        </div>
      </Header>
      {isBoardLoading ? (
        <PageLoadingSpinner />
      ) : (
        <BBITable data={newData} columns={columnConfig} />
      )}
    </>
  );
}

export default DatTruckBoard;
